import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { MediaType } from "@/types";
import SearchBar from "@/components/feature/SearchBar";
import MediaCard from "@/components/feature/MediaCard";
import NewsCard from "@/components/feature/NewsCard";
import { getLatestMedia, searchMedia, searchNews } from "@/lib/data";
import { useSeo } from "@/hooks/useSeo";

type TabKey = "all" | MediaType | "news";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
  { key: "news", label: "资讯" },
];

function Highlight({ text, keyword }: { text: string; keyword: string }) {
  const kw = keyword.trim();
  if (!kw) return <>{text}</>;
  const lower = text.toLowerCase();
  const target = kw.toLowerCase();
  const parts: { t: string; hit: boolean }[] = [];
  let cursor = 0;
  let idx = lower.indexOf(target);
  while (idx !== -1) {
    if (idx > cursor) parts.push({ t: text.slice(cursor, idx), hit: false });
    parts.push({ t: text.slice(idx, idx + kw.length), hit: true });
    cursor = idx + kw.length;
    idx = lower.indexOf(target, cursor);
  }
  if (cursor < text.length) parts.push({ t: text.slice(cursor), hit: false });
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <mark key={i} className="dv-mark">{p.t}</mark>
        ) : (
          <span key={i}>{p.t}</span>
        ),
      )}
    </>
  );
}

export default function Search() {
  const [params] = useSearchParams();
  const keyword = params.get("q") || "";
  const [tab, setTab] = useState<TabKey>("all");

  useSeo(
    keyword ? `“${keyword}”的搜索结果 - 榴莲视频` : "搜索 - 榴莲视频",
    `榴莲视频全站搜索，“${keyword}”相关的电影、电视剧、综艺与影视资讯结果。`,
    "榴莲视频,搜索,影视搜索",
  );

  const mediaResults = useMemo(() => searchMedia(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);

  const mediaFiltered = tab === "all" || tab === "news" ? mediaResults : mediaResults.filter((m) => m.type === tab);
  const showNews = tab === "all" || tab === "news";
  const totalCount = mediaFiltered.length + (showNews ? newsResults.length : 0);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
      <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">搜索结果</h1>
      <p className="mt-2 text-sm text-foreground-400">
        {keyword ? (
          <>
            关键词：
            <span className="text-accent-500">“{keyword}”</span>
            ，共找到 <span className="text-accent-500">{totalCount}</span> 条结果
          </>
        ) : (
          "输入关键词，搜索电影、电视剧、综艺与影视资讯"
        )}
      </p>

      <div className="mt-5 max-w-2xl">
        <SearchBar variant="hero" initialValue={keyword} />
      </div>

      <div className="mt-6 flex flex-wrap gap-1 rounded-full border border-background-200/60 bg-background-100/50 p-1 w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              tab === t.key ? "bg-primary-500 text-background-50" : "text-foreground-400 hover:text-foreground-950"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {totalCount > 0 ? (
        <div className="mt-8 space-y-12">
          {mediaFiltered.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-film-line text-accent-500"></i>
                影视作品 · {mediaFiltered.length}
              </h2>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5 lg:gap-6">
                {mediaFiltered.map((item, i) => (
                  <div key={item.id} className={`animate-fade-up delay-${(i % 4) + 1}`}>
                    <MediaCard item={item} showType />
                    <p className="mt-1 dv-clamp-1 text-xs text-foreground-300">
                      <Highlight text={item.title} keyword={keyword} />
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {showNews && newsResults.length > 0 && (
            <section>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-newspaper-line text-accent-500"></i>
                影视资讯 · {newsResults.length}
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {newsResults.map((n) => (
                  <NewsCard key={n.id} item={n} />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="mt-8">
          <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100/40 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-foreground-300">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg text-foreground-950">没有找到相关结果</p>
            <p className="mt-1 max-w-md text-sm text-foreground-400">
              {keyword ? `没有与“${keyword}”相关的内容，换个关键词试试` : "换个关键词，或浏览下方的热门推荐"}
            </p>
          </div>

          <section className="mt-12">
            <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-fire-line text-accent-500"></i>
              为你推荐
            </h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-5 lg:gap-6">
              {getLatestMedia(5).map((item) => (
                <MediaCard key={item.id} item={item} showType />
              ))}
            </div>
            <div className="mt-8 flex justify-center">
              <Link
                to="/"
                className="flex items-center gap-2 rounded-md border border-background-300/70 px-6 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-home-4-line"></i>
                返回首页浏览
              </Link>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}